import { DomainError } from '../errors/domain-error';
import {
  normalizeMarketingEditionBlocks,
  renderMarketingEditionHtml,
  renderMarketingEditionPlaintext,
  type MarketingEditionBlock,
} from './marketing-edition-blocks';

export const NEWSLETTER_CHECKOUT_FLOW = 'newsletter_subscription';

export const NewsletterPublicationAccessMode = {
  FREE: 'free',
  PAID: 'paid',
} as const;

export type NewsletterPublicationAccessModeValue =
  (typeof NewsletterPublicationAccessMode)[keyof typeof NewsletterPublicationAccessMode];

export const NEWSLETTER_PUBLICATION_ACCESS_MODE_VALUES = Object.values(
  NewsletterPublicationAccessMode,
);

export const NewsletterPaidInterval = {
  MONTHLY: 'monthly',
  ANNUAL: 'annual',
} as const;

export type NewsletterPaidIntervalValue =
  (typeof NewsletterPaidInterval)[keyof typeof NewsletterPaidInterval];

export const NEWSLETTER_PAID_INTERVAL_VALUES = Object.values(NewsletterPaidInterval);

export const NewsletterPaidSubscriptionStatus = {
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
} as const;

export type NewsletterPaidSubscriptionStatusValue =
  (typeof NewsletterPaidSubscriptionStatus)[keyof typeof NewsletterPaidSubscriptionStatus];

export function assertNewsletterAccessMode(value: string): NewsletterPublicationAccessModeValue {
  if (!(NEWSLETTER_PUBLICATION_ACCESS_MODE_VALUES as string[]).includes(value)) {
    throw new DomainError( 'Invalid publication access mode');
  }
  return value as NewsletterPublicationAccessModeValue;
}

export function assertNewsletterPaidInterval(value: string): NewsletterPaidIntervalValue {
  if (!(NEWSLETTER_PAID_INTERVAL_VALUES as string[]).includes(value)) {
    throw new DomainError( 'Paid interval must be monthly or annual');
  }
  return value as NewsletterPaidIntervalValue;
}

export function assertNewsletterPaidAmountCents(value: number): number {
  if (!Number.isInteger(value) || value < 100 || value > 1_000_000_00) {
    throw new DomainError( 'Paid amount must be between $1.00 and $1,000,000.00');
  }
  return value;
}

export function assertNewsletterFreePreviewBlocks(value: number): number {
  if (!Number.isInteger(value) || value < 0 || value > 20) {
    throw new DomainError( 'Free preview blocks must be 0–20');
  }
  return value;
}

export function grantsNewsletterPaidEntitlement(input: {
  accessMode: string;
  subscriptionStatus?: string | null;
  graceEndsAt?: Date | string | null;
  now?: Date;
}): boolean {
  if (input.accessMode !== NewsletterPublicationAccessMode.PAID) return true;
  const status = input.subscriptionStatus ?? null;
  if (status === NewsletterPaidSubscriptionStatus.ACTIVE) return true;
  if (status === NewsletterPaidSubscriptionStatus.PAST_DUE) {
    const grace = input.graceEndsAt ? new Date(input.graceEndsAt) : null;
    const now = input.now ?? new Date();
    return Boolean(grace && grace.getTime() > now.getTime());
  }
  return false;
}

/** Truncate allowlisted blocks for free preview when paywalled. */
export function buildNewsletterEditionPreview(input: {
  blocks: unknown;
  freePreviewBlocks: number;
}): {
  blocks: MarketingEditionBlock[];
  html: string;
  plaintext: string;
} {
  const all = normalizeMarketingEditionBlocks(input.blocks as never);
  const count = assertNewsletterFreePreviewBlocks(input.freePreviewBlocks);
  const blocks = all.slice(0, count);
  if (blocks.length === 0) {
    return {
      blocks: [],
      html: '<p>Subscribe to read this edition.</p>',
      plaintext: 'Subscribe to read this edition.',
    };
  }
  const brand = {
    name: 'Newsletter',
    fromName: null,
    brandColor: null,
    logoUrl: null,
    physicalAddress: null,
  };
  return {
    blocks,
    html: renderMarketingEditionHtml(blocks, brand),
    plaintext: renderMarketingEditionPlaintext(blocks, brand),
  };
}
